import React from 'react';

export const FREE_TRIAL_TOTAL = 30;
const STORAGE_KEY = 'ff:clinicals:freeUsed';

export function getFreeQuestionsUsed() {
    try { return Math.min(FREE_TRIAL_TOTAL, Number(localStorage.getItem(STORAGE_KEY) || 0)); } catch (_) { return 0; }
}

export function incrementFreeQuestionsUsed(n = 1) {
    try {
        const cur = getFreeQuestionsUsed();
        localStorage.setItem(STORAGE_KEY, String(cur + n));
    } catch (_) {}
}

export function hasFreeQuestionsRemaining() {
    return getFreeQuestionsUsed() < FREE_TRIAL_TOTAL;
}

/**
 * Banner shown above the Clinicals workspace for non-subscribers.
 * Props:
 *   questionsUsed   - number of free questions already used
 *   onUpgrade       - called when user clicks Upgrade
 *   onSignUp        - called when user clicks Sign Up (anonymous users)
 *   isAnonymous     - if true, shows sign-up CTA instead of upgrade
 */
const FreeTrialBanner = ({ questionsUsed = 0, onUpgrade, onSignUp, isAnonymous }) => {
    const remaining  = Math.max(0, FREE_TRIAL_TOTAL - questionsUsed);
    const pct        = (questionsUsed / FREE_TRIAL_TOTAL) * 100;
    const isExhausted = remaining === 0;

    if (isExhausted) {
        return (
            <div className="rounded-2xl border border-amber-400/20 bg-amber-500/5 p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-amber-300">Free Trial Complete</span>
                            <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full border border-amber-400/20">
                                {FREE_TRIAL_TOTAL}/{FREE_TRIAL_TOTAL} questions used
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed">
                            You've completed your {FREE_TRIAL_TOTAL} free questions.
                            {isAnonymous ? ' Create a free account then upgrade to unlock unlimited clinical vignettes.' : ' Upgrade to continue with unlimited questions.'}
                        </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        {isAnonymous && (
                            <button
                                onClick={onSignUp}
                                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs text-slate-200 hover:border-white/20 hover:text-white transition"
                            >
                                Sign up free
                            </button>
                        )}
                        <button
                            onClick={onUpgrade}
                            className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 text-xs font-medium text-white shadow-lg hover:opacity-95 transition"
                        >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Upgrade
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/5 p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span className="text-sm font-medium text-emerald-300">Free Trial — {remaining} free questions remaining</span>
                        </div>
                        <span className="text-xs text-slate-500">{questionsUsed}/{FREE_TRIAL_TOTAL}</span>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1.5 w-full rounded-full bg-slate-700/60">
                        <div
                            className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-500"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    {isAnonymous && (
                        <p className="text-[11px] text-slate-500 mt-1.5">
                            Sign up free to save your questions and get access to your full study history.
                        </p>
                    )}
                </div>
                <button
                    onClick={onUpgrade}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 px-4 py-2 text-xs font-medium text-emerald-200 hover:border-emerald-400/50 hover:text-white transition"
                >
                    <svg className="h-3.5 w-3.5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    Upgrade
                </button>
            </div>
        </div>
    );
};

export default FreeTrialBanner;
